import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Check, Dumbbell, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useEngine } from '@/contexts/EngineContext';
import { runProgramSwitch, clearPlanCaches, SWITCH_ERROR_MESSAGE } from '@/utils/programSwitch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ICONS = { coach: Brain, custom: Dumbbell };

// Curated programs carry their own `hero_image_url` from the API, so changing a
// preset's artwork updates the carousel, the sign-up step and this picker together.
const FALLBACK_HERO = {
  coach: '/program-coach-built.webp',
  custom: '/program-custom-build.webp',
};

const positionLine = (option) => {
  if (option.is_current) return 'Current program';
  if (option.saved) {
    const week = option.saved.week || 1;
    const days = option.saved.days_completed_this_week || 0;
    return `Saved — week ${week}${days ? `, ${days} day${days > 1 ? 's' : ''} done` : ''}`;
  }
  if (option.program_type === 'custom' && option.needs_builder) return 'Not built yet — opens the builder';
  return 'Start this program';
};

/** Program switcher: coach / custom build / curated programs, each resuming its own position. */
export const ProgramPicker = ({ api, open, onOpenChange }) => {
  const navigate = useNavigate();
  const engine = useEngine();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/programs/available');
      setOptions(res.data.options || []);
    } catch {
      toast.error('Could not load your programs');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const choose = async (option) => {
    if (option.is_current || switching) return;
    setSwitching(option.program_type);
    onOpenChange(false);
    const switchFn = async () => {
        const result = await runProgramSwitch(api, { target: option.program_type });
        if (result.needs_builder) return { redirect: '/custom-builder' };
        if (result.needs_regenerate) throw new Error(SWITCH_ERROR_MESSAGE);
        if (!result.switched) throw new Error(SWITCH_ERROR_MESSAGE);
        clearPlanCaches();
        return { redirect: '/workout', resumed: result.resumed, planName: result.plan_name };
    };
    engine.trigger({
      subtitle: `Building ${option.label}`,
      mode: 'change',
      generationFn: switchFn,
      onRetry: switchFn,
      onSuccess: (data) => {
        setSwitching(null);
        if (data?.redirect === '/custom-builder') {
          navigate('/custom-builder');
          return;
        }
        toast.success(data?.resumed
          ? `Back on ${data.planName || option.label} where you left off`
          : `Started ${option.label}`);
        navigate('/workout');
      },
      onError: () => {
        setSwitching(null);
        toast.error(SWITCH_ERROR_MESSAGE);
      },
      onDismiss: () => setSwitching(null),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#0F0F0F] border-white/10" data-testid="program-picker">
        <DialogHeader>
          <DialogTitle className="font-heading font-black uppercase tracking-tight text-lg">
            Switch program
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-white/50 -mt-2">
          Your progress is saved for every program — switch back any time and pick up where you left off.
        </p>

        {loading ? (
          <div className="py-10 flex justify-center" data-testid="program-picker-loading">
            <Loader2 className="w-5 h-5 animate-spin text-accent" />
          </div>
        ) : (
          <div className="space-y-2 max-h-[55vh] overflow-y-auto overscroll-contain pr-1">
            {options.map(option => {
              const Icon = ICONS[option.program_type] || Sparkles;
              const hero = option.hero_image_url || FALLBACK_HERO[option.program_type];
              const busy = switching === option.program_type;
              return (
                <button
                  key={option.program_type}
                  type="button"
                  onClick={() => choose(option)}
                  disabled={option.is_current || !!switching}
                  data-testid={`program-option-${option.program_type.replace(':', '-')}`}
                  className={`w-full text-left p-4 rounded-2xl border transition-colors flex items-center gap-3 ${
                    option.is_current
                      ? 'border-accent/60 bg-accent/10'
                      : 'border-white/10 bg-[#141414] hover:border-accent/40'
                  } disabled:cursor-default`}
                >
                  <span className="relative shrink-0 w-12 h-12 rounded-xl overflow-hidden border border-white/10 bg-white/[0.04]">
                    {hero ? (
                      <img src={hero} alt="" aria-hidden="true" loading="lazy" draggable={false}
                           className="w-full h-full object-cover" />
                    ) : (
                      <span className="w-full h-full grid place-items-center text-accent">
                        <Icon className="w-4 h-4" />
                      </span>
                    )}
                    {(busy || option.is_current) && (
                      <span className="absolute inset-0 grid place-items-center bg-black/55">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin text-accent" />
                              : <Check className="w-4 h-4 text-accent" />}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading font-bold uppercase text-sm tracking-wide text-white truncate">
                      {option.label}
                    </span>
                    <span className="block text-xs text-white/45 truncate">{option.description}</span>
                    <span className={`block text-xs mt-0.5 ${option.is_current ? 'text-accent' : 'text-white/60'}`}>
                      {positionLine(option)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProgramPicker;
